import type { DrugSuggestionGroup } from '@/hooks/use-drug-suggestions';

// Row model and commit-token helpers for the flat suggestion dropdown: one row
// per report the user can open. cmdk (shadcn Command) owns roving focus, roles
// and aria-activedescendant, so nothing here tracks keyboard state.

export type FlatRow = { key: string; group: DrugSuggestionGroup };

// A mixture over the resolver's component cap has no joint report: the row is
// a disabled item (cmdk skips it) and only its component chips commit.
export function isCommittableRow(group: DrugSuggestionGroup): boolean {
  return !(group.is_combination && group.reportable_together === false);
}

// Fold several same-substance mono groups into ONE commit row. `in_library`
// rolls up positive-only (true when ANY group is ready, false only when ALL
// confirmed a miss, else undefined). The match reason (brand/alias) survives
// the fold via the first group that carries one.
function mergeMonoBucket(bucket: DrugSuggestionGroup[]): DrugSuggestionGroup {
  const inLibrary = bucket.some((group) => group.in_library === true)
    ? true
    : bucket.every((group) => group.in_library === false)
      ? false
      : undefined;
  return {
    ...bucket[0],
    in_library: inLibrary,
    // No single route describes the merged row.
    route: null,
    matched_brand: bucket.find((group) => group.matched_brand)?.matched_brand,
    matched_alias: bucket.find((group) => group.matched_alias)?.matched_alias,
  };
}

function normalizeLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, ' ');
}

// In the WHO ATC system the 5th-level numbers 50-79 denote "combinations"
// classes shared by many different products (M01AE51 = ibuprofen,
// combinations), whereas lower numbers name one substance or one fixed
// combination (J01CR02 = amoxicillin + beta-lactamase inhibitor).
export function isCombinationClassAtc(code: string): boolean {
  const normalized = code.trim().toUpperCase();
  return isLevel5Atc(normalized) && Number(normalized.slice(5)) >= 50;
}

// Two combination groups are one report when they share a fixed-combination
// ATC (salt variants of Augmentin all sit under J01CR02), or — under a
// combinations-class ATC, which many unrelated products share — the same set
// of component substances.
function comboDedupeKey(group: DrugSuggestionGroup): string {
  const atc = group.atc.trim().toUpperCase();
  if (!isCombinationClassAtc(atc)) return `atc:${atc}`;
  const names = group.components?.length
    ? group.components.map((component) => component.substance)
    : group.substance.split(' + ');
  return `set:${names.map(normalizeLabel).filter(Boolean).sort().join('+')}`;
}

// A mono group and a combination group that carry the same label ("Ibuprofenum
// + Mentholum" tagged both ways by the registry) are one report. The
// combination wins so the row commits the component set and offers chips.
// `in_library` was computed from different aliases on each side, so it only
// survives when both agree — no status beats a possibly false "Raport gotowy".
function mergeCrossKind(
  mono: DrugSuggestionGroup,
  combo: DrugSuggestionGroup,
): DrugSuggestionGroup {
  return {
    ...combo,
    matched_brand: combo.matched_brand ?? mono.matched_brand,
    matched_alias: combo.matched_alias ?? mono.matched_alias,
    in_library:
      mono.in_library === combo.in_library ? combo.in_library : undefined,
  };
}

// Flatten the backend groups into the rendered rows, substances first, then
// combinations, backend order inside each section.
//
// Mono groups bucket by normalized label: the same substance under several
// ATC codes (oral / topical / IV) opens ONE umbrella report, so it gets one
// row. Salts ("Ibuprofenum lysinum") have a distinct label and stay separate.
//
// Combinations dedupe by `comboDedupeKey`. The first group keeps its slot; a
// later group with a ready report replaces it so the row can say so.
//
// A combination whose label matches a mono row folds into that row (see
// `mergeCrossKind`), keeping the mono row's slot.
export function buildFlatRows(groups: DrugSuggestionGroup[]): FlatRow[] {
  const monoBuckets = new Map<string, DrugSuggestionGroup[]>();
  const combosByKey = new Map<string, DrugSuggestionGroup>();

  for (const group of groups) {
    if (group.is_combination) {
      const key = comboDedupeKey(group);
      const kept = combosByKey.get(key);
      if (!kept || (group.in_library === true && kept.in_library !== true)) {
        combosByKey.set(key, group);
      }
    } else {
      const label = normalizeLabel(group.substance);
      const bucket = monoBuckets.get(label);
      if (bucket) {
        bucket.push(group);
      } else {
        monoBuckets.set(label, [group]);
      }
    }
  }

  const comboRows = [...combosByKey].map(([key, group]) => ({
    key: `combo:${key}`,
    group,
  }));
  const absorbed = new Set<string>();
  const substanceRows = [...monoBuckets].map(([label, bucket]) => {
    const mono = bucket.length > 1 ? mergeMonoBucket(bucket) : bucket[0];
    const twin = comboRows.find(
      (row) =>
        !absorbed.has(row.key) && normalizeLabel(row.group.substance) === label,
    );
    if (!twin) return { key: `sub:${label}`, group: mono };
    absorbed.add(twin.key);
    return { key: `sub:${label}`, group: mergeCrossKind(mono, twin.group) };
  });
  return [
    ...substanceRows,
    ...comboRows.filter((row) => !absorbed.has(row.key)),
  ];
}

// Order a combination's components so the one the user is typing surfaces first
// (exact, then prefix, then substring, then the rest), keeping the original order
// within each tier. Stable: identical scores keep registry order. A no-op for an
// empty query. Lets a large mixture's "pick one ingredient" list lead with the
// molecule that brought the mixture up.
export function orderComponentsByQuery<T extends { substance: string }>(
  components: T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return components;
  const rank = (component: T): number => {
    const s = component.substance.trim().toLowerCase();
    if (s === q) return 0;
    if (s.startsWith(q)) return 1;
    if (s.includes(q)) return 2;
    return 3;
  };
  return components
    .map((component, index) => ({ component, index, r: rank(component) }))
    .sort((a, b) => a.r - b.r || a.index - b.index)
    .map((entry) => entry.component);
}

// A full level-5 ATC code (e.g. N02BE01) — the only level the resolver can map to
// a single active substance. Levels 1–4 (e.g. N07AA) are broad classes and fail
// resolution, so they must never be committed as a search.
export function isLevel5Atc(code: string | null | undefined): boolean {
  return !!code && /^[A-Z]\d{2}[A-Z]{2}\d{2}$/.test(code.trim().toUpperCase());
}

// What to actually submit when a suggestion row is chosen: always the SUBSTANCE
// NAME (never the ATC code). Keeping every committed URL name-based makes mono and
// combination selections consistent — "Paracetamolum", "Amoksycylina + Acidum
// clavulanicum" — instead of mixing a bare code into a set of names. The resolver
// maps the name via RxNorm, and cache convergence is preserved server-side: a
// generated report still aliases its resolved ATC (mono) / component-name set
// (combination), so an ATC-code URL and the canonical name still hit one report.
export function commitToken(substance: string): string {
  return substance.trim();
}

// What to submit for "analyze together" on a combination: the component SUBSTANCE
// NAMES joined by " + ". The backend resolver splits on that separator and resolves
// EACH token, so the drug is analysed as one multi-substance unit (Augmentin →
// "Amoksycylina + Acidum clavulanicum" → amoxicillin + clavulanate). Mirrors the
// mono path (name-based), so the whole URL scheme is uniform. Falls back to the
// group's own joined label when it carries no component breakdown.
export function comboSetToken(
  combo: Pick<DrugSuggestionGroup, "substance" | "components">,
): string {
  const tokens = (combo.components ?? [])
    .map((component) => component.substance.trim())
    .filter(Boolean);
  return tokens.length > 0 ? tokens.join(" + ") : combo.substance.trim();
}

// Split a committed query into its component tokens (the combination forms
// "A + B" / "A/B" / "A,B"). Mirrors the backend's SYMBOL separators only — word
// connectors ("and" / "i" / "z" / "with") never appear in a committed URL and
// could chop a real substance name. A mono query yields a single token.
export function splitQueryTokens(query: string): string[] {
  return (query ?? "")
    .split(/\s*[+/&,]\s*/)
    .map((token) => token.trim())
    .filter(Boolean);
}

// True when a committed query carries at least one level-5 ATC code — the case
// that needs a registry lookup to recover a human label, because a bare code
// must never be shown in the search box.
export function hasAtcToken(query: string): boolean {
  return splitQueryTokens(query).some((token) => isLevel5Atc(token));
}

// Build the canonical URL path segment for a search query.
//
// A mono query is a single percent-encoded token. A combination is the SET of
// its component tokens — each encoded independently, sorted for a canonical order
// (so "A + B" and "B + A" share ONE url, hence one cached report), and joined by
// a single literal "+" separator. This keeps the segment unambiguous and free of
// encoded-space noise: "J01CA04+J01CR02", never "J01CA04%20%2B%20J01CR02".
export function toSearchSlug(query: string): string {
  const tokens = splitQueryTokens(query);
  if (tokens.length <= 1) {
    return encodeURIComponent((query ?? "").trim());
  }
  return [...tokens]
    .sort((a, b) => a.localeCompare(b))
    .map((token) => encodeURIComponent(token))
    .join("+");
}
